'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { builder } from '@builder.io/react';

import { TopNavLogo } from '@startengine/design-system.assets.images';
import { colors } from '@startengine/design-system.theme.main';

import useIsMobile from 'hooks/useIsMobile';
import useIsSignedIn from 'hooks/useIsSignedIn';
import useGetUser from 'hooks/useGetUser';
import useLoginModal from 'hooks/useLoginModal';
import useGetOffering from 'hooks/useGetOffering';
import useGetArticles from 'hooks/useGetArticles';
import usePaginatedOfferingUpdatesService from 'hooks/usePaginatedOfferingUpdatesService';
import useGetRewards from 'hooks/useGetRewards';
import useHasSpotlightClips from 'hooks/useHasSpotlightClips';
import { trackEvent } from 'utils/analytics';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Award, Briefcase, Settings, HelpCircle, LogOut } from 'lucide-react';
import FollowButton from 'components/Offering/HeroSection/Follow';
import { userMenu } from 'utils/links';
import { useBuilderABTest } from '../../BuilderABTestContext';
import { sectionBorderRadiusSx } from '../shared/sectionBorderRadius';
import useIsHydrated from '../shared/useIsHydrated';
import { SPOTLIGHTS_SECTION_ID, getSectionElement } from '../shared/sectionLookup';
import useIsEditorialReskin from '../shared/useIsEditorialReskin';
import { se3Colors, se3FontFamily } from '@startengine/design-system.se3.tokens';
import { COLORS } from '../../designTokens';
import { isTtwOffering } from '../shared/offeringStatus';

const STICKY_HEADER_HEIGHT = 64;

const NavOuterContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  zIndex: theme.zIndex.appBar,
  fontFamily: "'IBM Plex Sans', sans-serif",
}));

const NavStickyWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSticky' && prop !== 'topOffset',
})(({ isSticky, topOffset }) => ({
  position: isSticky ? 'fixed' : 'relative',
  top: isSticky ? topOffset : 'auto',
  left: 0,
  width: '100%',
  zIndex: isSticky ? 1100 : 10,
  backgroundColor: isSticky ? '#FBFDFA' : 'transparent',
  borderBottom: '2px solid #E5E7EB',
  overflow: 'visible',
  transition: 'box-shadow 0.3s ease-in-out',
  ...(isSticky && {
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
  }),
}));

const NavInner = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'stretch',
  maxWidth: '1700px',
  margin: '0 auto',
  width: '100%',
  minWidth: 0,
  height: `${STICKY_HEADER_HEIGHT}px`,
}));

const LogoSection = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  flex: '0 0 auto',
  minWidth: '180px',
  paddingLeft: '24px',
  '@media (max-width: 1199px)': {
    minWidth: '148px',
    paddingLeft: '16px',
  },
}));

const TabsContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'stretch',
  flex: '1 1 auto',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
}));

const TabsScroller = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'showScrollFade',
})(({ showScrollFade }) => ({
  display: 'flex',
  alignItems: 'stretch',
  flex: '1 1 auto',
  minWidth: 0,
  maxWidth: '100%',
  overflowX: 'auto',
  overflowY: 'hidden',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  gap: '0',
  ...(showScrollFade && {
    WebkitMaskImage: 'linear-gradient(to right, #000 calc(100% - 28px), transparent)',
    maskImage: 'linear-gradient(to right, #000 calc(100% - 28px), transparent)',
  }),
}));

const TabButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'isSticky',
})(({ isActive, isSticky }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: isSticky ? '14px 19px 0' : '14px 19px 10px',
  border: 'none',
  // eslint-disable-next-line no-nested-ternary
  borderBottom: isActive ? `4px solid ${colors.SEDark}` : isSticky ? 'none' : '2px solid #DDDDDD',
  background: 'none',
  cursor: 'pointer',
  fontFamily: "'IBM Plex Sans', sans-serif",
  fontSize: '14px',
  lineHeight: '16px',
  fontWeight: isActive ? 700 : 500,
  textTransform: 'uppercase',
  color: isActive ? colors.SEDark : 'inherit',
  whiteSpace: 'nowrap',
  flex: '0 0 auto',
  transition: 'color 0.2s, border-bottom 0.2s',
  '@media (max-width: 1199px)': {
    padding: isSticky ? '14px 12px 0' : '14px 12px 10px',
  },
  '@media (max-width: 899px)': {
    padding: isSticky ? '12px 12px 0' : '12px 12px 10px',
  },
  '&:hover': {
    color: colors.SEDark,
  },
}));

const RightSection = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  paddingRight: '24px',
  flex: '0 0 auto',
  minWidth: 0,
  justifyContent: 'flex-end',
  '& [data-cy="follow-desktop-button"]': {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  '& [data-cy="follow-desktop-button"] .MuiTypography-root': {
    whiteSpace: 'nowrap',
  },
  '@media (max-width: 1199px)': {
    gap: '8px',
    paddingRight: '16px',
  },
}));

const MobileTabsWrapper = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
}));

const UpdateDot = styled('span')(() => ({
  display: 'inline-block',
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: '#E06004',
  marginLeft: '5px',
}));

const PRIMARY_NEXT_ACTION_HREF = '/explore';

const DEFAULT_TABS = [
  { id: 'overview', label: 'Overview', sectionId: '' },
  { id: 'about', label: 'About', sectionId: 'about' },
  { id: 'terms', label: 'Terms', sectionId: 'terms' },
  { id: 'spotlights', label: 'Spotlights', sectionId: 'spotlights' },
  { id: 'press', label: 'Press & Updates', sectionId: 'press' },
  { id: 'rewards', label: 'Rewards', sectionId: 'rewards' },
  { id: 'discussion', label: 'Discussion', sectionId: 'discussion' },
  { id: 'faq', label: 'Investing FAQs', sectionId: 'faq' },
];

const NEXT_ACTION_TAB = { id: 'explore', label: 'Explore', href: PRIMARY_NEXT_ACTION_HREF };

const getTabId = (tab) => (tab.id || tab.sectionId || '').toLowerCase();

const BuilderClassicSectionNav = ({
  sectionId = '',
  sectionBorderRadius = '0px',
  tabs = [],
  showLogo = true,
  showFollowButton = true,
  backgroundColor = 'transparent',
  stickyBackgroundColor = '#FBFDFA',
  activeTabColor = '',
  tabTextColor = '',
  autoHideTabs = true,
  scrollOffset = 20,
}) => {
  const params = useParams();
  const searchParams = useSearchParams();
  const router = useRouter();
  const isMobile = useIsMobile();
  const slug = params?.slug?.[0];
  const abTest = useBuilderABTest();
  const signedIn = useIsSignedIn();
  const { data: userData } = useGetUser();
  const investmentAccounts = useSelector((state) => state.user.userAccounts);
  const investmentAccount = useMemo(
    () => investmentAccounts?.find((account) => account.accountStatus !== 'CLOSED') || null,
    [investmentAccounts],
  );
  const [renderLoginModal, setLoginModalOpen] = useLoginModal();
  const [accountOpen, setAccountOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const accountRef = useRef(null);

  const isMounted = useIsHydrated();
  const isEditorialReskin = useIsEditorialReskin();
  // Exact values from the Claude AtomBeam offering nav design.
  const editorialStickyBg = isEditorialReskin ? 'rgba(252, 249, 245, 0.92)' : stickyBackgroundColor;
  const editorialStickySx = isEditorialReskin
    ? {
        backdropFilter: 'saturate(1.8) blur(10px)',
        WebkitBackdropFilter: 'saturate(1.8) blur(10px)',
        borderBottom: '1px solid #E8E1D6',
        boxShadow: 'none',
      }
    : {};

  useEffect(() => {
    if (!accountOpen) return undefined;
    document.body.classList.add('account-dropdown-open');
    const handleClickOutside = (event) => {
      if (accountRef.current && !accountRef.current.contains(event.target)) {
        setAccountOpen(false);
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.classList.remove('account-dropdown-open');
    };
  }, [accountOpen]);

  const handleLogout = useCallback(() => {
    setAccountOpen(false);
    window.location.href = userMenu.logout;
  }, []);

  const isBuilderEditing =
    builder?.isEditing ||
    builder?.isPreviewing ||
    searchParams?.get('__builder_editing__') === 'true' ||
    searchParams?.has('builder.frameEditing');

  const { data: offeringData } = useGetOffering({ slug });
  const isTTW = isTtwOffering(offeringData);
  const isRegD =
    offeringData?.regulationType === 'Reg D' || offeringData?.regulationType === '506c';
  const commentsEnabled = offeringData?.commentsEnabled;

  const { data: rewardData } = useGetRewards({
    offeringId: offeringData?.id,
    enabled: Boolean(autoHideTabs),
  });
  const hasRewards = rewardData?.length > 0;

  const { data: pressData } = useGetArticles(offeringData?.id || offeringData?.primaryId);
  const noPress = !pressData?.pages?.[0]?.totalElements && !pressData?.totalElements;
  const hasPress = pressData?.pages?.[0]?.totalElements > 0 || pressData?.totalElements > 0;

  const { data: newUpdatesData } = usePaginatedOfferingUpdatesService({
    recentlyApproved: true,
    slug,
  });
  const { data: allUpdatesData } = usePaginatedOfferingUpdatesService({
    getAll: true,
    recentlyApproved: false,
    slug,
  });
  const hasNewUpdates = newUpdatesData?.pages?.[0]?.list?.length > 0;
  const hasUpdates = allUpdatesData?.pages?.[0]?.list?.length > 0;
  const noUpdates = !allUpdatesData?.pages?.[0]?.totalElements;

  const { hasClips: hasSpotlightClips } = useHasSpotlightClips(
    autoHideTabs ? offeringData?.primaryId || offeringData?.id : undefined,
  );

  // Determine which tabs to display.
  const resolvedTabs = useMemo(() => {
    const baseTabs = tabs.length > 0 ? tabs : DEFAULT_TABS;

    const shownTabs =
      !isMounted || !autoHideTabs || isBuilderEditing
        ? baseTabs
        : baseTabs.filter((tab) => {
            const tabId = getTabId(tab);
            if (tabId === 'terms' && (isTTW || isRegD)) return false;
            if (tabId === SPOTLIGHTS_SECTION_ID && !hasSpotlightClips) return false;
            if (tabId === 'press' && noPress && noUpdates && !hasNewUpdates) return false;
            if (tabId === 'rewards' && !hasRewards) return false;
            if (tabId === 'discussion' && !commentsEnabled) return false;
            return true;
          });

    if (isBuilderEditing) return shownTabs;
    if (
      shownTabs.some((tab) => tab.id === NEXT_ACTION_TAB.id || tab.href === NEXT_ACTION_TAB.href)
    ) {
      return shownTabs;
    }
    return [...shownTabs, NEXT_ACTION_TAB];
  }, [
    tabs,
    isMounted,
    autoHideTabs,
    isBuilderEditing,
    isTTW,
    isRegD,
    noPress,
    noUpdates,
    hasNewUpdates,
    hasRewards,
    commentsEnabled,
    hasSpotlightClips,
  ]);

  // Build dynamic label for press/updates tab.
  const getPressTabLabel = useCallback(() => {
    if (!isMounted) return '';
    if (noPress) {
      return noUpdates && !hasNewUpdates ? '' : 'Updates';
    }
    if (hasNewUpdates && hasPress) return 'Press & Updates';
    if (hasNewUpdates) return 'Updates';
    if (hasUpdates) return 'Press & Updates';
    return 'Press';
  }, [isMounted, noPress, noUpdates, hasNewUpdates, hasPress, hasUpdates]);

  // Sticky logic
  const navRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  // PD-36055 (Hannah): on the editorial offering page the section nav is a
  // persistent top bar — always the full logo + tabs + account header, fixed to
  // the top — instead of inline tabs that only become a header after the user
  // scrolls past the "sticky nav section". `stickyActive` drives the full-header
  // layout; `persistentTopNav` keeps it fixed regardless of scroll position.
  const persistentTopNav = isEditorialReskin;
  const stickyActive = persistentTopNav || isSticky;
  const [selectedTabId, setSelectedTabId] = useState('overview');
  const isClickScrolling = useRef(false);
  const tabButtonRefs = useRef({});

  // Track the nav's position when it first renders (delayed to ensure layout is stable)
  const navInitialTop = useRef(null);

  useEffect(() => {
    const capturePosition = () => {
      if (navRef.current) {
        navInitialTop.current = navRef.current.getBoundingClientRect().top + window.scrollY;
      }
    };
    // Delay capture to allow page layout to settle
    const timer = setTimeout(capturePosition, 300);
    return () => clearTimeout(timer);
  }, []);

  const stickNavbar = useCallback(() => {
    if (navInitialTop.current === null) return;
    // PD-34298 defense-in-depth: when a modal is open the design-system
    // useBodyScrollLock pins body to `position: fixed; top: -SCROLLY`, which
    // makes window.scrollY read 0 even though the user has not scrolled. If
    // we honored that 0 we'd flip isSticky → false, unmount the sticky nav
    // (and any modal mounted as its child), which on cleanup would restore
    // scroll → fire another scroll event → flip isSticky → true → remount,
    // forming an infinite mount/unmount loop. Skip the update while body is
    // scroll-locked; the next genuine scroll after the modal closes will
    // bring isSticky back into sync.
    if (typeof document !== 'undefined' && document.body.style.position === 'fixed') {
      return;
    }
    setIsSticky(window.scrollY >= navInitialTop.current);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', stickNavbar, { passive: true });
    return () => window.removeEventListener('scroll', stickNavbar);
  }, [stickNavbar]);

  // Scroll-based active tab tracking
  useEffect(() => {
    const sectionIds = resolvedTabs.map((tab) => tab.sectionId).filter(Boolean);
    const totalOffset = STICKY_HEADER_HEIGHT + scrollOffset + 10;

    const handleScroll = () => {
      if (isClickScrolling.current) return;

      let activeTab = null;

      for (const id of sectionIds) {
        const el = getSectionElement(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= totalOffset) {
          activeTab = id;
        }
      }

      if (activeTab) {
        const matchingTab = resolvedTabs.find((tab) => tab.sectionId === activeTab);
        if (matchingTab) {
          setSelectedTabId(matchingTab.id || matchingTab.sectionId);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [resolvedTabs, scrollOffset]);

  useEffect(() => {
    if (!isMobile) return;
    const activeTabButton = tabButtonRefs.current[selectedTabId];
    if (typeof activeTabButton?.scrollIntoView !== 'function') return;
    activeTabButton.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [isMobile, selectedTabId]);

  const handleTabClick = useCallback(
    (tab) => {
      const tabId = tab.id || tab.sectionId || 'overview';
      setSelectedTabId(tabId);
      isClickScrolling.current = true;

      trackEvent('classic_section_nav_tab_clicked', {
        tab_id: tabId,
        tab_label: tab.label,
        offering_slug: slug,
      });
      abTest?.trackConversion('section_nav_clicked', { tab_id: tabId });

      if (tab.href) {
        router.push(tab.href);
        isClickScrolling.current = false;
        return;
      }

      if (!tab.sectionId || tab.sectionId === '') {
        window.scrollTo({ top: 0 });
        isClickScrolling.current = false;
        return;
      }

      const sId = tab.sectionId;
      const element = getSectionElement(sId);
      if (element) {
        element.style.scrollMarginTop = `${STICKY_HEADER_HEIGHT + 10}px`;
        element.scrollIntoView();
      }

      setTimeout(() => {
        isClickScrolling.current = false;
      }, 100);
    },
    [slug, abTest, router],
  );

  const resolvedActiveColor = isEditorialReskin ? '#0B292D' : activeTabColor || colors.SEDark;
  const resolvedTabTextColor = isEditorialReskin ? '#4C463E' : tabTextColor || 'inherit';

  const renderTabs = () =>
    resolvedTabs.map((tab) => {
      const tabId = tab.id || tab.sectionId || 'overview';
      const isActive = selectedTabId === tabId;
      const isPressTab = (tab.id || tab.sectionId || '').toLowerCase() === 'press';

      const label = isPressTab ? getPressTabLabel() || tab.label : tab.label;
      const displayLabel =
        isMounted && (tab.id || tab.sectionId || '').toLowerCase() === 'faq' && isRegD
          ? 'How It Works'
          : label;

      const editorialStyle = isEditorialReskin
        ? {
            fontFamily: se3FontFamily.sans,
            letterSpacing: '1.84px',
            fontSize: '11.5px',
            fontWeight: 600,
            // Gold active underline per the Claude design (inline + sticky).
            borderBottom: isActive ? '2px solid #B58A4B' : '2px solid transparent',
          }
        : {};

      return (
        <TabButton
          key={tabId}
          isActive={isActive}
          isSticky={isSticky}
          onClick={() => handleTabClick(tab)}
          data-cy={`classic-section-nav-tab-${tabId}`}
          ref={(node) => {
            if (node) {
              tabButtonRefs.current[tabId] = node;
            } else {
              delete tabButtonRefs.current[tabId];
            }
          }}
          style={{
            color: isActive ? resolvedActiveColor : resolvedTabTextColor,
            ...editorialStyle,
          }}
        >
          {displayLabel}
          {isMounted && isPressTab && hasNewUpdates && (
            <UpdateDot aria-label="New updates available" />
          )}
        </TabButton>
      );
    });

  // Desktop layout
  if (!isMobile) {
    return (
      <NavOuterContainer
        id={sectionId || undefined}
        ref={navRef}
        data-cy="builder-classic-section-nav"
        sx={{
          backgroundColor,
          minHeight: !persistentTopNav && isSticky ? `${STICKY_HEADER_HEIGHT}px` : 'auto',
          ...sectionBorderRadiusSx(sectionBorderRadius, { clip: false }),
        }}
      >
        {/* Non-sticky: tabs shown inline with page content */}
        {!stickyActive && (
          <Box sx={{ mt: 4, maxWidth: '1240px', mx: 'auto', overflow: 'visible' }}>
            <TabsScroller aria-label="Offering sections" data-cy="classic-section-nav-tabs">
              {renderTabs()}
            </TabsScroller>
          </Box>
        )}

        {/* Sticky / persistent: full header with logo + tabs + follow */}
        {stickyActive && (
          <NavStickyWrapper
            isSticky
            topOffset={isEditorialReskin && isTTW ? 'var(--ttw-banner-offset, 0px)' : '0px'}
            data-cy="builder-classic-section-nav-sticky"
            sx={{ backgroundColor: editorialStickyBg, pt: '12px', ...editorialStickySx }}
          >
            <NavInner sx={{ height: '64px' }}>
              {showLogo && (
                <LogoSection>
                  <a
                    href={process.env.NEXT_PUBLIC_ROOT_URL || '/'}
                    style={{ textDecoration: 'none' }}
                    aria-label="StartEngine"
                  >
                    <TopNavLogo
                      sx={{ height: '40px', width: '86px', cursor: 'pointer' }}
                      color={isEditorialReskin ? se3Colors.ink : colors.SEDark}
                      data-cy={
                        isEditorialReskin ? 'classic-nav-logo-editorial' : 'classic-nav-logo'
                      }
                    />
                  </a>
                </LogoSection>
              )}
              <TabsContainer>
                <TabsScroller
                  showScrollFade
                  aria-label="Offering sections"
                  data-cy="classic-section-nav-tabs"
                  sx={{
                    // PD (Paige QA): the editorial persistent nav clipped the
                    // first tab ("OVERVIEW" → "VIEW") because the negative left
                    // margin pulled the strip under the logo. Zero it for the
                    // editorial reskin so the first tab starts cleanly; control's
                    // sticky nav keeps its original alignment (untouched).
                    ml: persistentTopNav ? '0px' : showLogo ? { md: '-24px', lg: '-60px' } : '0px',
                    scrollPaddingLeft: persistentTopNav
                      ? '0px'
                      : showLogo
                        ? { md: '24px', lg: '60px' }
                        : '0px',
                    scrollPaddingRight: '24px',
                  }}
                >
                  {renderTabs()}
                </TabsScroller>
              </TabsContainer>
              <RightSection>
                {showFollowButton && <FollowButton />}
                <Box ref={accountRef} sx={{ position: 'relative' }}>
                  <Box
                    component="button"
                    onClick={() => {
                      if (signedIn) {
                        setAccountOpen((p) => !p);
                      } else {
                        setLoginModalOpen(true);
                      }
                    }}
                    data-cy="section-nav-account-button"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundColor: 'transparent',
                      color: isEditorialReskin ? se3Colors.ink : colors.SEDark,
                      padding: '6px 12px',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: isEditorialReskin
                        ? se3FontFamily.sans
                        : "'IBM Plex Sans', sans-serif",
                      fontSize: '14px',
                      lineHeight: '16px',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      '&:hover': { opacity: 0.7 },
                    }}
                  >
                    {signedIn && <PersonOutlineIcon sx={{ fontSize: 20 }} />}
                    {signedIn ? '▾' : 'Log In'}
                  </Box>
                  {accountOpen && signedIn && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        right: 0,
                        width: '280px',
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                        border: '1px solid #e5e7eb',
                        overflow: 'hidden',
                        zIndex: 99999,
                      }}
                      data-cy="section-nav-account-dropdown"
                    >
                      <Box sx={{ p: '16px' }}>
                        <Box
                          sx={{ fontWeight: 600, color: '#1f2937' }}
                          className="pii"
                          data-block-recording
                        >
                          {userData?.firstName || userData?.email?.split('@')[0] || ''}
                        </Box>
                        {!investmentAccount && (
                          <Box
                            component="a"
                            href={`${process.env.NEXT_PUBLIC_ROOT_URL || ''}/account`}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              mt: 0.5,
                              fontSize: '14px',
                              fontWeight: 500,
                              color: COLORS.primary,
                              textDecoration: 'none',
                              maxWidth: '200px',
                              flexWrap: 'wrap',
                            }}
                          >
                            Open your investment account to invest ›
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ borderBottom: '2px dotted #d4d0cb', mx: 2, mt: 0.5, mb: 1 }} />
                      {[
                        {
                          label: 'Venture Club',
                          url: userMenu.ventureClub || '/venture-club',
                          Icon: Award,
                          iconColor: '#F59E0B',
                        },
                        { label: 'Portfolio', url: userMenu.portfolio, Icon: Briefcase },
                        { label: 'Settings', url: userMenu.settings, Icon: Settings },
                        {
                          label: 'Help Center',
                          url: 'https://help.startengine.com/',
                          Icon: HelpCircle,
                        },
                      ].map((item) => (
                        <Box
                          key={item.label}
                          component="button"
                          onClick={() => {
                            setAccountOpen(false);
                            window.open(item.url, '_blank', 'noopener,noreferrer');
                          }}
                          sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            p: '12px 16px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontFamily: 'inherit',
                            fontSize: '14px',
                            color: '#1f2937',
                            '&:hover': { backgroundColor: 'rgba(24,92,101,0.08)' },
                          }}
                        >
                          <item.Icon
                            style={{ height: 16, width: 16, color: item.iconColor || '#6b7280' }}
                          />
                          {item.label}
                        </Box>
                      ))}
                      <Box
                        component="button"
                        onClick={() => {
                          setAccountOpen(false);
                          handleLogout();
                        }}
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          p: '12px 16px',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontFamily: 'inherit',
                          fontSize: '14px',
                          color: '#1f2937',
                          '&:hover': { backgroundColor: 'rgba(24,92,101,0.08)' },
                        }}
                      >
                        <LogOut style={{ height: 16, width: 16, color: '#6b7280' }} />
                        Log Out
                      </Box>
                    </Box>
                  )}
                </Box>
              </RightSection>
            </NavInner>
          </NavStickyWrapper>
        )}
        {/* PD-34298: registered at top-level (not inside the {isSticky && ...}
            branch) so the call site stays mounted regardless of sticky state.
            With the LoginModalProvider, this no-op stub just registers the
            (empty) config; the real <LoginModal> lives in the provider. */}
        {renderLoginModal({})}
      </NavOuterContainer>
    );
  }

  // Mobile layout
  return (
    <NavOuterContainer
      id={sectionId || undefined}
      ref={navRef}
      data-cy="builder-classic-section-nav"
      sx={{
        backgroundColor,
        minHeight: !persistentTopNav && isSticky ? `${STICKY_HEADER_HEIGHT}px` : 'auto',
        ...sectionBorderRadiusSx(sectionBorderRadius, { clip: false }),
      }}
    >
      <NavStickyWrapper
        isSticky={stickyActive}
        topOffset={isEditorialReskin && isTTW ? 'var(--ttw-banner-offset, 0px)' : '0px'}
        data-cy="builder-classic-section-nav-sticky"
        sx={{
          backgroundColor: stickyActive ? editorialStickyBg : backgroundColor,
          height: stickyActive ? `${STICKY_HEADER_HEIGHT}px` : 'auto',
          ...(stickyActive ? editorialStickySx : {}),
        }}
      >
        <MobileTabsWrapper
          sx={{
            mt: 0,
            height: '100%',
            position: 'relative',
          }}
        >
          <IconButton
            onClick={() => setCategoriesOpen(true)}
            sx={{ flexShrink: 0, color: '#111827', p: 0.5, ml: 0.5 }}
            data-cy="mobile-section-nav-hamburger"
            aria-label="Open categories"
            aria-haspopup="dialog"
            aria-expanded={categoriesOpen}
            aria-controls={categoriesOpen ? 'section-categories-dialog' : undefined}
          >
            <MenuIcon sx={{ fontSize: 22 }} aria-hidden="true" />
          </IconButton>
          <TabsScroller
            showScrollFade
            aria-label="Offering sections"
            data-cy="classic-section-nav-tabs"
            sx={{
              flex: 1,
              minWidth: 0,
              mr: stickyActive && showFollowButton ? '64px' : 0,
              scrollPaddingLeft: '48px',
              scrollPaddingRight: stickyActive && showFollowButton ? '96px' : '16px',
              '& button': {
                height: stickyActive ? '44px' : '50px',
              },
            }}
          >
            {renderTabs()}
          </TabsScroller>
          {stickyActive && showFollowButton && (
            <Box
              className="section-nav-follow"
              sx={{
                position: 'absolute',
                right: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                flexShrink: 0,
                '& .MuiSvgIcon-root, & svg': {
                  '&&': {
                    width: '20px',
                    height: '20px',
                    minWidth: 'unset',
                    minHeight: 'unset',
                    padding: '2px',
                    backgroundColor: 'transparent',
                    boxSizing: 'content-box',
                    borderRadius: 0,
                  },
                },
              }}
            >
              <FollowButton />
            </Box>
          )}
        </MobileTabsWrapper>
      </NavStickyWrapper>

      <Dialog
        open={categoriesOpen}
        onClose={() => setCategoriesOpen(false)}
        fullScreen
        aria-labelledby="section-categories-title"
        sx={{ zIndex: 9999 }}
        PaperProps={{ id: 'section-categories-dialog', sx: { backgroundColor: '#FFFFFF' } }}
      >
        <Box sx={{ p: 3, pt: 4 }}>
          <IconButton
            onClick={() => setCategoriesOpen(false)}
            sx={{ position: 'absolute', top: 16, left: 16 }}
            aria-label="Close categories"
          >
            <CloseIcon sx={{ fontSize: 24, color: '#111827' }} aria-hidden="true" />
          </IconButton>
          <Typography
            id="section-categories-title"
            sx={{ fontSize: '32px', fontWeight: 700, color: '#111827', mt: 5, mb: 3 }}
          >
            CATEGORIES
          </Typography>
          {resolvedTabs.map((tab, i) => {
            const tabId = tab.id || tab.sectionId || 'overview';
            const isPressTab = tabId.toLowerCase() === 'press';
            const label = isPressTab ? getPressTabLabel() || tab.label : tab.label;
            return (
              <Box key={tabId}>
                <Box
                  onClick={() => {
                    setCategoriesOpen(false);
                    setTimeout(() => handleTabClick(tab), 300);
                  }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 3,
                    cursor: 'pointer',
                  }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                    {label}
                  </Typography>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      backgroundColor: '#F3F4F6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <span aria-hidden="true">→</span>
                  </Box>
                </Box>
                {i < resolvedTabs.length - 1 && (
                  <Box sx={{ height: '1px', backgroundColor: '#E5E7EB' }} />
                )}
              </Box>
            );
          })}
          <Box sx={{ height: '80px' }} />
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              backgroundColor: '#FFFFFF',
            }}
          >
            <Button
              onClick={() => setCategoriesOpen(false)}
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: COLORS.primary,
                color: '#FFFFFF',
                borderRadius: '16px',
                textTransform: 'none',
                fontWeight: 600,
                py: 1.5,
                fontSize: '16px',
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
    </NavOuterContainer>
  );
};

export default BuilderClassicSectionNav;
